import camelcaseKeys from 'camelcase-keys';
import types from '../mutation-types';
import ProductsAPI from '../../api/products';
import ProductOrdersAPI from '../../api/productOrders';
import { throwErrorMessage } from '../utils/api';

export const state = {
  products: [],
  productOrders: [],
  uiFlags: {
    isFetchingProducts: false,
    isFetchingProductOrders: false,
    isCreatingProduct: false,
    isUpdatingProduct: false,
    isDeletingProduct: false,
    isCreatingProductOrder: false,
    isUpdatingProductOrder: false,
    isDeletingProductOrder: false,
    isStartingProductOrder: false,
    isAdvancingProductOrder: false,
    isCancelingProductOrder: false,
    isCompletingProductOrder: false,
    isExecutingProductOrderAction: false,
  },
};

export const getters = {
  getProducts($state) {
    return [...$state.products].sort((productA, productB) => {
      if (productA.active !== productB.active) {
        return productA.active ? -1 : 1;
      }

      return productA.name.localeCompare(productB.name);
    });
  },
  getProductOrders($state) {
    return [...$state.productOrders].sort(
      (orderA, orderB) =>
        new Date(orderB.createdAt).getTime() -
        new Date(orderA.createdAt).getTime()
    );
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

const upsertRecord = (records, record) => {
  const index = records.findIndex(item => item.id === record.id);
  if (index === -1) {
    return [record, ...records];
  }

  return records.map(item => (item.id === record.id ? record : item));
};

export const actions = {
  async getProducts({ commit }) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isFetchingProducts: true });
    try {
      const response = await ProductsAPI.get();
      commit(
        types.SET_PRODUCTS,
        camelcaseKeys(response.data.payload, { deep: true })
      );
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isFetchingProducts: false });
    }
  },
  async createProduct({ commit }, product) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isCreatingProduct: true });
    try {
      const response = await ProductsAPI.create({ product });
      commit(
        types.ADD_PRODUCT,
        camelcaseKeys(response.data.payload, { deep: true })
      );
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isCreatingProduct: false });
    }
  },
  async updateProduct({ commit }, { id, ...product }) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isUpdatingProduct: true });
    try {
      const response = await ProductsAPI.update(id, { product });
      commit(
        types.EDIT_PRODUCT,
        camelcaseKeys(response.data.payload, { deep: true })
      );
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isUpdatingProduct: false });
    }
  },
  async deleteProduct({ commit }, id) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isDeletingProduct: true });
    try {
      await ProductsAPI.delete(id);
      commit(types.DELETE_PRODUCT, id);
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isDeletingProduct: false });
    }
  },
  async getProductOrders({ commit }) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isFetchingProductOrders: true });
    try {
      const response = await ProductOrdersAPI.get();
      commit(
        types.SET_PRODUCT_ORDERS,
        camelcaseKeys(response.data.payload, { deep: true })
      );
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, {
        isFetchingProductOrders: false,
      });
    }
  },
  async createProductOrder({ commit }, productOrder) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isCreatingProductOrder: true });
    try {
      const response = await ProductOrdersAPI.create({
        product_order: productOrder,
      });
      commit(
        types.ADD_PRODUCT_ORDER,
        camelcaseKeys(response.data.payload, { deep: true })
      );
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, {
        isCreatingProductOrder: false,
      });
    }
  },
  async updateProductOrder({ commit }, { id, ...productOrder }) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isUpdatingProductOrder: true });
    try {
      const response = await ProductOrdersAPI.update(id, {
        product_order: productOrder,
      });
      commit(
        types.EDIT_PRODUCT_ORDER,
        camelcaseKeys(response.data.payload, { deep: true })
      );
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, {
        isUpdatingProductOrder: false,
      });
    }
  },
  async deleteProductOrder({ commit }, id) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isDeletingProductOrder: true });
    try {
      await ProductOrdersAPI.delete(id);
      commit(types.DELETE_PRODUCT_ORDER, id);
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, {
        isDeletingProductOrder: false,
      });
    }
  },
  async startProductOrder({ commit }, id) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isStartingProductOrder: true });
    try {
      const response = await ProductOrdersAPI.start(id);
      commit(
        types.EDIT_PRODUCT_ORDER,
        camelcaseKeys(response.data.payload, { deep: true })
      );
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, {
        isStartingProductOrder: false,
      });
    }
  },
  async completeProductOrder({ commit }, id) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, {
      isCompletingProductOrder: true,
    });
    try {
      const response = await ProductOrdersAPI.complete(id);
      commit(
        types.EDIT_PRODUCT_ORDER,
        camelcaseKeys(response.data.payload, { deep: true })
      );
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, {
        isCompletingProductOrder: false,
      });
    }
  },
  async advanceProductOrder({ commit }, id) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isAdvancingProductOrder: true });
    try {
      const response = await ProductOrdersAPI.advance(id);
      commit(
        types.EDIT_PRODUCT_ORDER,
        camelcaseKeys(response.data.payload, { deep: true })
      );
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, {
        isAdvancingProductOrder: false,
      });
    }
  },
  async cancelProductOrder({ commit }, id) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, { isCancelingProductOrder: true });
    try {
      const response = await ProductOrdersAPI.cancel(id);
      commit(
        types.EDIT_PRODUCT_ORDER,
        camelcaseKeys(response.data.payload, { deep: true })
      );
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, {
        isCancelingProductOrder: false,
      });
    }
  },
  async executeProductOrderAction({ commit }, actionId) {
    commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, {
      isExecutingProductOrderAction: true,
    });
    try {
      const response = await ProductOrdersAPI.executeAction(actionId);
      commit(
        types.EDIT_PRODUCT_ORDER,
        camelcaseKeys(response.data.payload, { deep: true })
      );
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_PRODUCTS_ORDERS_UI_FLAG, {
        isExecutingProductOrderAction: false,
      });
    }
  },
};

export const mutations = {
  [types.SET_PRODUCTS_ORDERS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_PRODUCTS]($state, products) {
    $state.products = products;
  },
  [types.ADD_PRODUCT]($state, product) {
    $state.products = upsertRecord($state.products, product);
  },
  [types.EDIT_PRODUCT]($state, product) {
    $state.products = upsertRecord($state.products, product);
  },
  [types.DELETE_PRODUCT]($state, id) {
    $state.products = $state.products.filter(product => product.id !== id);
  },
  [types.SET_PRODUCT_ORDERS]($state, productOrders) {
    $state.productOrders = productOrders;
  },
  [types.ADD_PRODUCT_ORDER]($state, productOrder) {
    $state.productOrders = upsertRecord($state.productOrders, productOrder);
  },
  [types.EDIT_PRODUCT_ORDER]($state, productOrder) {
    $state.productOrders = upsertRecord($state.productOrders, productOrder);
  },
  [types.DELETE_PRODUCT_ORDER]($state, id) {
    $state.productOrders = $state.productOrders.filter(
      productOrder => productOrder.id !== id
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
